import {
  resolveWorkBillingAnalyticsProps,
  trackWorkEvent,
  WorkLaunchEventName,
} from '@/models/analytics/features/work';
// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- app composition invalidating an existing session cache identity read from the inert /contracts door (the rule anchors on the first import, hence the placement)
import { copilotRunContextByProjectAtom } from '@/features/copilot/copilot-run-sessions';
import {
  usePublishNotePushApplyCore,
  type PublishNotePushApplyHost,
} from '@heidi/agents';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { useWorkspaceId } from '@heidi/workspace/data';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

/** `work_artifact_actioned` for an accepted apply, with the Work billing columns. */
const trackArtifactApplied: PublishNotePushApplyHost['trackArtifactApplied'] = (props) =>
  trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
    ...props,
    ...resolveWorkBillingAnalyticsProps(),
  });

/**
 * Publish the note-push apply bridge while the chat is mounted, bound to the
 * app: the ambient workspace, the copilot escalation's session for the run, the
 * note view's revision, the note re-fetch and the Work emit.
 */
export const usePublishNotePushApply = (
  params: Parameters<typeof usePublishNotePushApplyCore>[0]
): void => {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const runContextByProject = useAtomValue(copilotRunContextByProjectAtom);
  const sessionId = runContextByProject[params.projectId]?.sessionId ?? null;
  // The note view's opaque revision token, echoed VERBATIM into the plan so the
  // backend can fail-closed when the note changed under the confirmed mapping.
  // A READ-ONLY observer over the session view the main window already fetches
  // (the note view / active-note publisher own the fetch — `skipToken` never
  // fetches here): reactive, so the plan built at submit time carries the token
  // of the note version on screen, including after the stale-409 re-fetch below.
  const { data: noteRevision } = useQuery<
    QuerySessionResponse | undefined,
    Error,
    string | null
  >({
    queryKey: [GET_SESSION_BY_ID, { sessionId: sessionId ?? '' }],
    queryFn: skipToken,
    select: (session) => session?.consult_note?.updated_at ?? null,
  });
  // The note changed under the user: re-fetch so the panel and the NEXT confirm
  // bind the current note and its revision.
  const onStaleNoteRevision = useCallback(
    (staleSessionId: string) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: staleSessionId }],
      });
    },
    [queryClient]
  );

  usePublishNotePushApplyCore(params, {
    workspaceId,
    sessionId,
    noteRevision,
    onStaleNoteRevision,
    trackArtifactApplied,
  });
};
